import feature1 from "../../../assets/feature1.webp";
import feature2 from "../../../assets/feature2.webp";
import feature3 from "../../../assets/feature3.webp";
import feature4 from "../../../assets/feature4.webp";
import feature5 from "../../../assets/feature5.webp";
import feature6 from "../../../assets/feature6.webp";
import useReveal from "../../../hooks/useReveal";

const screenshots = [
	{
		image: feature1,
		alt: "Racketly Apple Watch live score screen alongside pickleball match setup on iPhone",
		title: "Score in one tap, from the wrist",
		description:
			"Keep the match moving. Check the score, log a point and correct a tap without reaching for your phone.",
		highlight: "Live scoring",
	},
	{
		image: feature2,
		alt: "Racketly iPhone match insights screen showing score and performance statistics after a racket sports match",
		title: "Match insights that explain the result",
		description:
			"See the match beyond the final score, with the details you need to notice what changed on court.",
		highlight: "Match insights",
	},
	{
		image: feature3,
		alt: "Racketly match setup screen on iPhone with sport, scoring format and tie-break options",
		title: "Your rules, already understood",
		description:
			"Set up singles, doubles, practice or competition with the scoring format that matches how you play.",
		highlight: "Flexible formats",
	},
	{
		image: feature4,
		alt: "Racketly sport picker showing tennis, padel, pickleball, badminton, squash and ping pong",
		title: "Every court, one history",
		description:
			"Tennis, Padel, Pickleball, Badminton, Squash, and Ping Pong. Switch sports instantly, keep one history.",
		highlight: "Six sports",
	},
	{
		image: feature5,
		alt: "Racketly statistics overview on iPhone showing matches, wins, losses, swings and calories",
		title: "See the numbers behind your game",
		description:
			"Review your matches, wins and losses, with stats for swings and calories across your racket sports.",
		highlight: "Your progress",
	},
	{
		image: feature6,
		alt: "Racketly performance trend chart on iPhone with match activity, distance and time played",
		title: "Follow your progress over time",
		description:
			"See your performance trend alongside match activity, distance and time played.",
		highlight: "Performance trends",
	},
];

function ScreenshotCard({ screenshot, index, isDark }) {
	const ref = useReveal(index % 3);
	const ink = isDark ? "var(--color-on-night)" : "var(--color-ink)";
	const muted = isDark ? "var(--color-on-night-muted)" : "var(--color-muted)";

	return (
		<div ref={ref} className="reveal flex flex-col">
			<figure
				className="lift mb-5 self-start"
				style={{
					border: `var(--rule-fine) solid ${isDark ? "var(--color-night-rule)" : "var(--color-ink)"}`,
					borderRadius: "var(--radius-card)",
					overflow: "hidden",
					width: "min(100%, 260px)",
					flexShrink: 0,
				}}
			>
				<img
					src={screenshot.image}
					alt={screenshot.alt}
					className="w-full h-auto block"
					width="780"
					height="1688"
					loading="lazy"
					decoding="async"
				/>
			</figure>

			<p
				className="font-display font-bold uppercase mb-2"
				style={{ fontSize: "var(--text-xs)", letterSpacing: "var(--tracking-label)", color: "var(--color-accent)" }}
			>
				{screenshot.highlight}
			</p>
			<h3
				className="font-display font-bold mb-2"
				style={{ fontSize: "var(--text-xl)", color: ink, overflowWrap: "anywhere" }}
			>
				{screenshot.title}
			</h3>
			<p
				style={{
					fontSize: "var(--text-sm)",
					lineHeight: "var(--lh-body)",
					color: muted,
					maxWidth: "var(--measure)",
				}}
			>
				{screenshot.description}
			</p>
		</div>
	);
}

const ScreenshotsSection = ({ isDark }) => {
	const headRef = useReveal(0);
	const surface = isDark ? "var(--color-night)" : "var(--color-paper)";
	const ink = isDark ? "var(--color-on-night)" : "var(--color-ink)";
	const muted = isDark ? "var(--color-on-night-muted)" : "var(--color-muted)";

	return (
		<section id="screenshots" className="py-20 md:py-28" style={{ background: surface }}>
			<div className="container mx-auto px-4 sm:px-6">
				<div ref={headRef} className="reveal mb-14 max-w-2xl">
					<p
						className="font-display font-bold uppercase mb-3"
						style={{ fontSize: "var(--text-sm)", letterSpacing: "var(--tracking-label)", color: "var(--color-accent)" }}
					>
						Inside the app
					</p>
					<h2
						className="font-display font-bold uppercase"
						style={{
							fontSize: "var(--text-display-s)",
							lineHeight: "var(--lh-tight)",
							letterSpacing: "var(--tracking-display)",
							color: ink,
							overflowWrap: "anywhere",
						}}
					>
						Built for the court, obsessed with the details
					</h2>
					<p className="mt-4" style={{ fontSize: "var(--text-lg)", color: muted }}>
						Everything you see here is part of Racketly — from live wrist scoring to the match history you’ll want to keep.
					</p>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
					{screenshots.map((screenshot, index) => (
						<ScreenshotCard key={screenshot.title} screenshot={screenshot} index={index} isDark={isDark} />
					))}
				</div>
			</div>
		</section>
	);
};

export default ScreenshotsSection;
